import { Platform, Linking } from 'react-native';
import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';
import { supabase } from './supabase';
import { getLogger } from '../utils/logger';
import { userActions } from '../state/globalState';

// Logger para as funções de links de autenticação
const logger = getLogger('AUTH-LINKS');

/**
 * Extrai parâmetros de uma URL (suporta tanto query params quanto fragmentos)
 */
const getParamsFromURL = (url: string): Record<string, string> => {
  const params: Record<string, string> = {};
  
  try {
    // Verificar se tem fragmento ou query params
    const hashIndex = url.indexOf('#');
    const queryIndex = url.indexOf('?');
    
    // Extrair string de parâmetros (após # ou ?)
    let paramString = '';
    if (hashIndex >= 0) {
      paramString = url.substring(hashIndex + 1);
    } else if (queryIndex >= 0) {
      paramString = url.substring(queryIndex + 1);
    } else {
      return params; // Sem parâmetros
    }
    
    // Separar cada par key=value e adicionar ao objeto de resultado
    paramString.split('&').forEach(part => {
      const [key, value] = part.split('=');
      if (key && value) {
        params[key] = decodeURIComponent(value.replace(/\+/g, ' '));
      }
    });
  } catch (error) {
    logger.error('Erro ao extrair parâmetros da URL:', error);
  }
  
  return params;
};

/**
 * Retorna a URL apropriada para redirecionamento de autenticação
 * No mobile: esquema deep link
 * Na web: URL baseada no ambiente (dev ou prod)
 */
export const getURL = (): string => {
  logger.info('Obtendo URL para redirecionamento de autenticação');
  
  if (Platform.OS !== 'web') {
    // No mobile, retorna o URL scheme para deep link
    const scheme = Constants.expoConfig?.scheme;
    if (!scheme) {
      const fallbackScheme = 'appguincho';
      logger.warn(`Nenhum scheme encontrado no app.json, usando "${fallbackScheme}" como padrão`);
      return `${fallbackScheme}://`;
    }
    logger.info(`Usando scheme: ${scheme}://`);
    return `${scheme}://`;
  } else {
    // Na web, usa localhost para dev ou o domínio real para produção
    const isProd = process.env.NODE_ENV === 'production';
    const host = isProd ? window.location.origin : 'http://localhost:8081';
    logger.info(`Usando host para web: ${host}`);
    return host;
  }
};

/**
 * Processa uma URL de autenticação
 */
export const processAuthURL = async (url: string): Promise<boolean> => {
  if (!url) return false;
  
  logger.info(`Processando URL de autenticação: ${url}`);
  
  // Verificar se é URL de autenticação
  const isAuthURL = url.includes('access_token=') ||
    url.includes('refresh_token=') ||
    url.includes('type=recovery') ||
    url.includes('type=signup') ||
    url.includes('token=') ||
    url.includes('code=');
    
  if (!isAuthURL) {
    logger.info('URL não é de autenticação');
    return false;
  }
  
  try {
    // Extrair parâmetros da URL
    const params = getParamsFromURL(url);
    
    // Log de parâmetros de forma segura (não mostrar tokens completos)
    const safeParams = { ...params };
    if (safeParams.access_token) {
      safeParams.access_token = safeParams.access_token.substring(0, 8) + '...';
    }
    if (safeParams.refresh_token) {
      safeParams.refresh_token = safeParams.refresh_token.substring(0, 8) + '...';
    }
    if (safeParams.token) {
      safeParams.token = safeParams.token.substring(0, 8) + '...';
    }
    if (safeParams.code) {
      safeParams.code = safeParams.code.substring(0, 8) + '...';
    }
    logger.info(`Parâmetros extraídos: ${JSON.stringify(safeParams)}`);
    
    // Verificar o tipo de URL de autenticação
    const hasSignupVerification = params.token && params.type === 'signup';
    const hasTokens = params.access_token && params.refresh_token;
    const hasCode = params.code;
    const isRecovery = params.type === 'recovery' || url.includes('type=recovery');
    
    // Caso 1: URL contém tokens de acesso e refresh
    if (hasTokens) {
      logger.info('Processando URL com tokens de acesso/refresh');
      
      // Verificar se é um link de recuperação de senha
      if (isRecovery) {
        logger.info('Link identificado como recuperação de senha');
        userActions.setPasswordRecoveryMode(true);
      }
      
      const { data, error } = await supabase.auth.setSession({
        access_token: params.access_token,
        refresh_token: params.refresh_token,
      });
      
      if (error) {
        logger.error('Erro ao configurar sessão a partir dos tokens:', error);
        return false;
      }
      
      if (data.session) {
        logger.info('Sessão configurada a partir dos tokens com sucesso');
        userActions.setSession(data.session);
      }
      
      return true;
    }
    // Caso 2: Verificação de email após signup com token
    else if (hasSignupVerification) {
      logger.info('Processando URL de verificação de email (token)');
      
      const { data, error } = await supabase.auth.verifyOtp({
        token_hash: params.token,
        type: 'signup',
      });
      
      if (error) {
        logger.error('Erro ao verificar OTP:', error);
        return false;
      }
      
      if (data.session) {
        logger.info('Email verificado com sucesso, sessão iniciada');
        userActions.setSession(data.session);
      } else {
        logger.warn('Email verificado mas sessão não iniciada');
      }
      
      return true;
    }
    // Caso 3: Verificação com parâmetro 'code'
    else if (hasCode) {
      logger.info('Processando URL com código de autenticação');
      
      // Verificar se é um link de recuperação de senha
      if (isRecovery) {
        logger.info('Link de código identificado como recuperação de senha');
        userActions.setPasswordRecoveryMode(true);
      }
      
      const { data, error } = await supabase.auth.exchangeCodeForSession(params.code);
      
      if (error) {
        logger.error('Erro ao trocar código por sessão:', error);
        return false;
      }
      
      if (data.session) {
        logger.info('Código verificado com sucesso, sessão obtida');
        userActions.setSession(data.session);
      } else {
        logger.warn('Código verificado mas sessão não iniciada');
      }
      
      return true;
    }
    // Caso 4: Outros casos, tentar refresh de sessão
    else {
      logger.info('Tentando refresh de sessão');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        logger.error('Erro ao fazer refresh da sessão:', error);
      } else if (data.session) {
        logger.info('Sessão atualizada com sucesso via refresh');
        userActions.setSession(data.session);
      }
      
      return !error;
    }
  } catch (error) {
    logger.error('Erro ao processar URL de autenticação:', error);
    return false;
  }
};

/**
 * Configura os deep links para autenticação no mobile
 * Deve ser chamado no useEffect da tela de login/signup ou no RootLayout
 */
export const setupAuthDeepLinks = () => {
  if (Platform.OS === 'web') {
    logger.info('setupAuthDeepLinks: Processando link de autenticação na web');
    // WEB: extrair parâmetros da URL e setar modo de recuperação se necessário
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      const fullUrl = window.location.href;
      logger.info(`[setupAuthDeepLinks] URL completa: ${fullUrl}`);
      const searchParams = new URLSearchParams(window.location.search);
      const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
      const hashParams = new URLSearchParams(hash);
      const typeRecovery = searchParams.get('type') === 'recovery' || hashParams.get('type') === 'recovery';
      if (typeRecovery) {
        logger.info('[setupAuthDeepLinks] Detectado type=recovery na URL, ativando modo de recuperação de senha');
        userActions.setPasswordRecoveryMode(true);
      }
    }
    return () => {}; // Retorna função vazia para cleanup
  }

  logger.info('Configurando listeners para deep links de autenticação');
  
  // Registrar o listener para URL de inicialização (caso o app seja aberto pelo link)
  let urlSubscription: any = null;
  
  Linking.getInitialURL().then((url) => {
    if (url) {
      logger.info(`URL inicial encontrada: ${url}`);
      processAuthURL(url);
    } else {
      logger.info('Nenhuma URL inicial encontrada');
    }
  }).catch(err => {
    logger.error('Erro ao obter URL inicial:', err);
  });

  // Registrar o listener para URLs recebidas enquanto o app está aberto
  urlSubscription = Linking.addEventListener('url', ({ url }) => {
    logger.info(`Nova URL recebida enquanto app está aberto: ${url}`);
    processAuthURL(url);
  });

  // Retornar função de cleanup
  return () => {
    if (urlSubscription) {
      urlSubscription.remove();
      logger.info('Listener de URL removido');
    }
  };
};

/**
 * Abre um link de autenticação usando o browser interno
 * Útil para OAuth e outros fluxos que requerem um navegador
 */
export const openAuthLink = async (url: string): Promise<void> => {
  logger.info(`Abrindo link de autenticação: ${url}`);
  
  try {
    if (Platform.OS !== 'web') {
      // No mobile, use o WebBrowser para manter o contexto do app
      await WebBrowser.openAuthSessionAsync(url, getURL());
    } else {
      // Na web, abra em uma nova aba
      window.open(url, '_blank');
    }
  } catch (error) {
    logger.error('Erro ao abrir link de autenticação:', error);
  }
}; 
